import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BrandMark } from "@bht/ui";
import { ThemeToggle } from "./ThemeToggle.js";
import { FeedbackButton } from "./FeedbackWidget.js";
import { ProfileDrawer } from "./ProfileDrawer.js";

/**
 * Bottom-anchored navigation bar for the mobile app shell (Slack / Whoop
 * style: fixed to the bottom edge, one icon per destination with a short
 * label beneath it so the target is named, not guessed).
 *
 * Four primary destinations + a "More" overflow that opens a bottom sheet,
 * so the bar never crowds past five slots as we add surfaces. Primary is the
 * daily loop (Home · Practice · Review · Mastery); the sheet holds the
 * secondary + cross-app links (Hidden Test · Library · Vyzrly).
 *
 * Mobile-only — rendered by AppShell when viewport !== "desktop"; desktop
 * uses the DesktopShell left sidebar. Hidden in drill-focus mode. AppShell
 * pads the scroll content by the bar height so nothing hides behind it.
 */

export const MOBILE_TAB_BAR_HEIGHT = 58;

type Dest = {
  key: string;
  label: string;
  href: string;
  glyph: () => React.ReactNode;
};

// Primary tabs (always visible). Same four the desktop sidebar leads with, so
// the app teaches one structure on both: the daily loop plus the Library.
const PRIMARY: Dest[] = [
  // The BRAND MARK, not a third symbol. Home had a crosshair logo, a house
  // icon and this bullseye all on screen at once. The mark carries the branding
  // that left the top bar with it, and the visible HOME label supplies meaning.
  { key: "home", label: "Home", href: "/app/home", glyph: () => <BrandGlyph /> },
  { key: "practice-test", label: "Practice", href: "/app/practice-test", glyph: () => <ClipboardGlyph /> },
  { key: "mistakes", label: "Review", href: "/app/mistakes", glyph: () => <ReplayGlyph /> },
  { key: "packs", label: "Library", href: "/app/packs", glyph: () => <PackGlyph /> },
];

// Secondary + cross-app links — reached via the "More" sheet. Hidden Test also
// has its own card on Home, so it lives here rather than taking a primary slot.
const MORE: Dest[] = [
  { key: "mastery", label: "Mastery", href: "/app/mastery", glyph: () => <ChartGlyph /> },
  { key: "hidden-test", label: "Hidden Test", href: "/app/hidden-test", glyph: () => <PlayGlyph /> },
  { key: "faq", label: "How it works", href: "/faq", glyph: () => <HelpGlyph /> },
  { key: "blog", label: "Journal", href: "/blog/", glyph: () => <JournalGlyph /> },
  { key: "vyzrly", label: "Vyzrly", href: "/app/vyzrly", glyph: () => <VyzrlyGlyph /> },
];

const MORE_KEYS = new Set(MORE.map((d) => d.key));

/**
 * Is this one of the five destinations the tab bar itself owns?
 *
 * Exported so AppShell can drop the top strip on exactly these screens without
 * keeping a second copy of the list. A root tab screen needs no top bar: the
 * bar below it already says where you are, and the page renders its own
 * heading. A top bar earns its space on NESTED screens, where there is a Back
 * and a screen title to carry.
 */
export function isRootTabRoute(pathname: string): boolean {
  // EXACT, not startsWith. The tab bar highlights with a prefix match, which is
  // right for "you are somewhere under Practice", but suppressing the header on
  // a prefix would strip it from /app/practice-test/run/:id and
  // /app/practice-test/report/:id too. Those are nested screens: they are
  // exactly where a top bar has a job, and they would have lost the only way
  // back that is not the browser's own.
  const p = pathname.replace(/\/+$/, "") || "/";
  return PRIMARY.some((d) => p === d.href);
}

function activeKey(pathname: string): string {
  if (pathname.startsWith("/app/practice-test")) return "practice-test";
  if (pathname.startsWith("/app/mistakes")) return "mistakes";
  if (pathname.startsWith("/app/mastery")) return "mastery";
  if (pathname.startsWith("/app/packs")) return "packs";
  if (pathname.startsWith("/blog")) return "blog";
  if (pathname.startsWith("/app/vyzrly")) return "vyzrly";
  if (pathname.startsWith("/app/hidden-test")) return "hidden-test";
  if (pathname.startsWith("/app/home") || pathname.startsWith("/app/drill")) return "home";
  return "";
}

export function MobileTabBar() {
  const { pathname } = useLocation();
  const active = activeKey(pathname);
  const [moreOpen, setMoreOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  // "More" reads active whenever the current route lives inside the sheet.
  const moreActive = moreOpen || MORE_KEYS.has(active);

  return (
    <>
      {moreOpen && (
        <MoreSheet
          active={active}
          onClose={() => setMoreOpen(false)}
          onOpenProfile={() => setProfileOpen(true)}
        />
      )}
      {/* The bar owns the drawer now. It used to be mounted by AppHeader, which
          no longer renders on root tab screens, so the account would have been
          unreachable from exactly the five screens students spend their time
          on. */}
      <ProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} />
      <nav
        aria-label="Primary"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 30,
          display: "flex",
          height: MOBILE_TAB_BAR_HEIGHT,
          paddingBottom: "env(safe-area-inset-bottom)",
          borderTop: "1px solid var(--border-line)",
          background: "var(--bg-1)",
        }}
      >
        {PRIMARY.map((t) => (
          <TabLink key={t.key} dest={t} active={t.key === active} onNavigate={() => setMoreOpen(false)} />
        ))}
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          aria-label="More"
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            minWidth: 0,
            border: "none",
            background: "none",
            cursor: "pointer",
            color: moreActive ? "var(--accent-amber)" : "var(--fg-2)",
          }}
        >
          <span style={{ lineHeight: 0 }}><MenuGlyph /></span>
          <TabLabel>More</TabLabel>
        </button>
      </nav>
    </>
  );
}

function TabLink({ dest, active, onNavigate }: { dest: Dest; active: boolean; onNavigate: () => void }) {
  const color = active ? "var(--accent-amber)" : "var(--fg-2)";
  return (
    <Link
      to={dest.href}
      aria-label={dest.label}
      aria-current={active ? "page" : undefined}
      onClick={onNavigate}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        minWidth: 0,
        color,
        textDecoration: "none",
        /* NO amber bar across the top edge. The selected tab already carries
           an amber icon and an amber label, which reads clearly on its own; a
           third signal drawn as a segment above one fifth of the bar looks like
           a progress or page-position indicator rather than navigation state. */
      }}
    >
      <span style={{ lineHeight: 0 }}>{dest.glyph()}</span>
      <TabLabel>{dest.label}</TabLabel>
    </Link>
  );
}

function TabLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

/** Bottom sheet of overflow destinations, opened from the "More" tab. */
function MoreSheet({
  active,
  onClose,
  onOpenProfile,
}: {
  active: string;
  onClose: () => void;
  onOpenProfile: () => void;
}) {
  return (
    <>
      <div
        onClick={onClose}
        aria-hidden
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 40,
          background: "rgba(0,0,0,0.45)",
        }}
      />
      <div
        role="menu"
        aria-label="More destinations"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 41,
          background: "var(--bg-1)",
          borderTop: "1px solid var(--border-line)",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          padding: "8px 12px",
          paddingBottom: "max(16px, env(safe-area-inset-bottom))",
        }}
      >
        <div
          aria-hidden
          style={{
            width: 36,
            height: 4,
            borderRadius: 999,
            background: "var(--border-strong)",
            margin: "6px auto 12px",
          }}
        />
        {/* SETTINGS, not destinations, and they sit below the links because of
            it. Both used to live in the top bar, where they charged permanent
            navigation chrome for things a student touches once a term, if ever.
            This sheet is the app's one overflow surface, and a second menu in
            the opposite corner would only make somebody learn which
            miscellaneous things belong to which. */}
        {MORE.map((d) => {
          const isActive = d.key === active;
          return (
            <Link
              key={d.key}
              to={d.href}
              role="menuitem"
              onClick={onClose}
              aria-current={isActive ? "page" : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "14px 10px",
                borderRadius: 8,
                textDecoration: "none",
                color: isActive ? "var(--fg-0)" : "var(--fg-1)",
                background: isActive ? "var(--bg-2)" : "transparent",
              }}
            >
              <span style={{ lineHeight: 0, color: isActive ? "var(--accent-amber)" : "var(--fg-2)" }}>
                {d.glyph()}
              </span>
              <span style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 500 }}>
                {d.label}
              </span>
            </Link>
          );
        })}
        {/* ACCOUNT, reached from here rather than from a second control in the
            opposite corner. The drawer itself is unchanged: it holds the exam
            target, the target editor, admin and sign out, and rebuilding all of
            that into this sheet would risk sign out to save one screen. What
            mattered was that there stopped being two competing menus. */}
        <button
          type="button"
          onClick={() => {
            onClose();
            onOpenProfile();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            width: "100%",
            padding: "14px 10px",
            marginTop: 8,
            borderTop: "1px solid var(--border-line)",
            background: "none",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            textAlign: "left",
            color: "var(--fg-1)",
          }}
        >
          <span style={{ lineHeight: 0, color: "var(--fg-2)" }}>
            <AccountGlyph />
          </span>
          <span style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 500 }}>
            Account and target
          </span>
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            paddingTop: 8,
            borderTop: "1px solid var(--border-line)",
          }}
        >
          <FeedbackButton />
          <ThemeToggle />
        </div>
      </div>
    </>
  );
}

// ───────────────── Glyphs (match the DesktopShell sidebar set) ─────────────

/** The real brand mark, at tab-icon size. */
function BrandGlyph() {
  return <BrandMark size={20} />;
}

function AccountGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
    </svg>
  );
}

function TargetGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

function ClipboardGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="15" y2="16" />
    </svg>
  );
}

function ReplayGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}

function ChartGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 4 4 5-5" />
    </svg>
  );
}

function PackGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    </svg>
  );
}

function PlayGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

function JournalGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H19v14H5.5A1.5 1.5 0 0 0 4 19.5z" />
      <line x1="7.5" y1="8" x2="15.5" y2="8" />
      <line x1="7.5" y1="11" x2="15.5" y2="11" />
      <line x1="7.5" y1="14" x2="12.5" y2="14" />
    </svg>
  );
}

function HelpGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.3a2.8 2.8 0 0 1 5.3 1.1c0 1.8-2.6 2.3-2.6 4" />
      <line x1="12" y1="17" x2="12" y2="17.01" />
    </svg>
  );
}

function MenuGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function VyzrlyGlyph() {
  // Partner mark keeps its own brand color (no currentColor tint), reading
  // as an "external app" cue.
  return (
    <img src="/logo/vyzrly-mark.png" alt="" width={20} height={20} style={{ borderRadius: 4, display: "block" }} />
  );
}
